import { useCallback, useEffect, useRef, useState } from 'react';
import { useTonConnectUI, useTonWallet, type ConnectAdditionalRequest } from '@tonconnect/ui-react';
import type { TonNetwork, Wallet } from '@tb/shared';
import { api } from '../api/endpoints';
import { RequestError } from '../api/client';
import { notify } from '../telegram/telegram';
import { useSession } from './SessionProvider';

export type WalletState =
  | { status: 'disconnected' }
  | { status: 'connecting' }
  | { status: 'verifying' }
  | { status: 'ready'; wallet: Wallet }
  | { status: 'error'; message: string };

export function useWallet(): WalletState & {
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
} {
  const [tonConnectUI] = useTonConnectUI();
  const wallet = useTonWallet();
  const session = useSession();
  const [state, setState] = useState<WalletState>({ status: 'disconnected' });
  const verifiedAddress = useRef<string | null>(null);

  useEffect(() => {
    if (wallet) return;

    let cancelled = false;
    const settle = (value: { state: 'ready'; value: ConnectAdditionalRequest } | null): void => {
      if (!cancelled) tonConnectUI.setConnectRequestParameters(value);
    };

    tonConnectUI.setConnectRequestParameters({ state: 'loading' });

    if (session.status === 'ready') {
      void (async () => {
        try {
          const { payload } = await api.auth.tonProofPayload(AbortSignal.timeout(10_000));
          settle({ state: 'ready', value: { tonProof: payload } });
        } catch {
          settle(null);
        }
      })();
    } else if (session.status !== 'loading') {
      settle(null);
    }

    return () => {
      cancelled = true;
      tonConnectUI.setConnectRequestParameters(null);
    };
  }, [tonConnectUI, wallet, session.status]);

  useEffect(() => {
    if (!wallet) {
      verifiedAddress.current = null;
      setState({ status: 'disconnected' });
      return;
    }

    if (verifiedAddress.current === wallet.account.address) return;

    const proofItem = wallet.connectItems?.tonProof;
    if (!proofItem || !('proof' in proofItem)) {
      void (async () => {
        setState({ status: 'verifying' });
        try {
          const wallets = await api.auth.wallets();
          const known = wallets.find((entry) => entry.address === wallet.account.address);
          if (known) {
            verifiedAddress.current = wallet.account.address;
            setState({ status: 'ready', wallet: known });
          } else {
            await tonConnectUI.disconnect();
            setState({ status: 'disconnected' });
          }
        } catch {
          setState({ status: 'disconnected' });
        }
      })();
      return;
    }

    void (async () => {
      setState({ status: 'verifying' });
      try {
        const verified = await api.auth.verifyWallet({
          address: wallet.account.address,
          network: chainToNetwork(wallet.account.chain),
          publicKey: wallet.account.publicKey ?? '',
          walletStateInit: wallet.account.walletStateInit,
          proof: {
            timestamp: proofItem.proof.timestamp,
            domain: proofItem.proof.domain,
            signature: proofItem.proof.signature,
            payload: proofItem.proof.payload,
          },
        });

        verifiedAddress.current = wallet.account.address;
        notify('success');
        setState({ status: 'ready', wallet: verified });
      } catch (error) {
        notify('error');
        const message =
          error instanceof RequestError
            ? error.message
            : 'The wallet could not be confirmed. Try connecting again.';
        await tonConnectUI.disconnect().catch(() => undefined);
        setState({ status: 'error', message });
      }
    })();
  }, [wallet, tonConnectUI]);

  const connect = useCallback(async () => {
    setState({ status: 'connecting' });
    try {
      await tonConnectUI.openModal();
    } catch {
      setState({ status: 'disconnected' });
    }
  }, [tonConnectUI]);

  const disconnect = useCallback(async () => {
    verifiedAddress.current = null;
    await tonConnectUI.disconnect().catch(() => undefined);
    setState({ status: 'disconnected' });
  }, [tonConnectUI]);

  return { ...state, connect, disconnect };
}

function chainToNetwork(chain: string): TonNetwork {
  return chain === '-239' ? 'mainnet' : 'testnet';
}
